import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { PhysicsWorld } from '../physics/PhysicsWorld';
import { EventBus } from '../core/EventBus';

export interface JumpRampConfig {
  position: THREE.Vector3;
  rotation: number; // Y rotation in radians
  rampLength: number;
  rampHeight: number;
  rampWidth: number;
  hoopDistance: number; // Distance from ramp end to hoop
  hoopHeight: number; // Height of hoop above ground
}

export class JumpRamp {
  private scene: THREE.Scene;
  private physicsWorld: PhysicsWorld;
  private mesh: THREE.Group;
  private body: CANNON.Body;
  private hoopMesh: THREE.Group;
  private hoopTriggerRadius: number = 3;
  private config: JumpRampConfig;
  private hoopPassed: boolean = false;

  constructor(scene: THREE.Scene, physicsWorld: PhysicsWorld, config: JumpRampConfig) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
    this.config = config;

    this.mesh = this.createRampMesh();
    this.body = this.createRampBody();
    this.hoopMesh = this.createHoopMesh();

    this.mesh.position.copy(config.position);
    this.mesh.rotation.y = config.rotation;
    scene.add(this.mesh);

    physicsWorld.addBody(this.body);

    // Position hoop in front of ramp
    const hoopOffset = new THREE.Vector3(0, 0, config.rampLength / 2 + config.hoopDistance);
    hoopOffset.applyAxisAngle(new THREE.Vector3(0, 1, 0), config.rotation);
    this.hoopMesh.position.copy(config.position).add(hoopOffset);
    this.hoopMesh.position.y = config.hoopHeight;
    this.hoopMesh.rotation.y = config.rotation;
    scene.add(this.hoopMesh);
  }

  private createRampMesh(): THREE.Group {
    const group = new THREE.Group();
    const { rampLength, rampHeight, rampWidth } = this.config;

    // Use a simple rotated box for the ramp surface - much easier to reason about
    const rampAngle = Math.atan2(rampHeight, rampLength);
    const rampHypotenuse = Math.sqrt(rampLength * rampLength + rampHeight * rampHeight);

    // Main ramp surface - a flat box rotated to create the slope
    const surfaceGeometry = new THREE.BoxGeometry(rampWidth, 0.3, rampHypotenuse);
    const surfaceMaterial = new THREE.MeshStandardMaterial({
      color: 0x2a2a4e,
      metalness: 0.7,
      roughness: 0.3,
    });
    const surface = new THREE.Mesh(surfaceGeometry, surfaceMaterial);
    // Position at center of slope and rotate to match angle
    surface.position.set(0, rampHeight / 2, 0);
    surface.rotation.x = -rampAngle;
    group.add(surface);

    // Front wall (vertical face at the high end)
    const wallGeometry = new THREE.BoxGeometry(rampWidth, rampHeight, 0.2);
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a3e,
      metalness: 0.6,
      roughness: 0.4,
    });
    const frontWall = new THREE.Mesh(wallGeometry, wallMaterial);
    frontWall.position.set(0, rampHeight / 2, rampLength / 2);
    group.add(frontWall);

    // Neon edge strips along the sides of the ramp surface
    const edgeMaterial = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 2,
    });

    // Left edge strip (along ramp slope)
    const leftStrip = new THREE.Mesh(
      new THREE.BoxGeometry(0.15, 0.15, rampHypotenuse),
      edgeMaterial
    );
    leftStrip.position.set(-rampWidth / 2 + 0.1, rampHeight / 2, 0);
    leftStrip.rotation.x = -rampAngle;
    group.add(leftStrip);

    // Right edge strip
    const rightStrip = new THREE.Mesh(
      new THREE.BoxGeometry(0.15, 0.15, rampHypotenuse),
      edgeMaterial.clone()
    );
    rightStrip.position.set(rampWidth / 2 - 0.1, rampHeight / 2, 0);
    rightStrip.rotation.x = -rampAngle;
    group.add(rightStrip);

    // Top edge glow (at the launch point)
    const topEdge = new THREE.Mesh(
      new THREE.BoxGeometry(rampWidth, 0.2, 0.2),
      new THREE.MeshStandardMaterial({
        color: 0xff00ff,
        emissive: 0xff00ff,
        emissiveIntensity: 2,
      })
    );
    topEdge.position.set(0, rampHeight + 0.15, rampLength / 2 - 0.1);
    group.add(topEdge);

    // Bottom entry edge
    const bottomEdge = new THREE.Mesh(
      new THREE.BoxGeometry(rampWidth, 0.15, 0.15),
      edgeMaterial.clone()
    );
    bottomEdge.position.set(0, 0.15, -rampLength / 2 + 0.1);
    group.add(bottomEdge);

    // Arrow indicators on ramp surface
    const arrowMaterial = new THREE.MeshStandardMaterial({
      color: 0x00ff00,
      emissive: 0x00ff00,
      emissiveIntensity: 1.5,
    });

    for (let i = 0; i < 3; i++) {
      const arrowGeom = new THREE.ConeGeometry(0.4, 0.8, 3);
      arrowGeom.rotateX(-Math.PI / 2 - rampAngle);
      const arrow = new THREE.Mesh(arrowGeom, arrowMaterial);
      const t = (i + 1) / 4;
      // Position arrows along the slope
      const arrowZ = -rampLength / 2 + t * rampLength;
      const arrowY = t * rampHeight + 0.4;
      arrow.position.set(0, arrowY, arrowZ);
      group.add(arrow);
    }

    return group;
  }

  private createRampBody(): CANNON.Body {
    const { position, rotation } = this.config;

    // Create a dummy body - ramp physics handled via height calculation
    // This avoids collision conflicts that cause the bike to get stuck
    const body = new CANNON.Body({
      mass: 0,
      type: CANNON.Body.STATIC,
    });

    // Small marker shape (not used for collision)
    const markerShape = new CANNON.Box(new CANNON.Vec3(0.1, 0.1, 0.1));
    body.addShape(markerShape);

    body.position.set(position.x, 0, position.z);
    body.quaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), rotation);

    // Disable collision - we handle ramps via getHeightAtPosition()
    body.collisionFilterGroup = 0;
    body.collisionFilterMask = 0;

    return body;
  }

  // Check if a position is on this ramp and return the height it should be at
  public getHeightAtPosition(worldPos: THREE.Vector3): number | null {
    const { position, rotation, rampLength, rampHeight, rampWidth } = this.config;

    // Transform world position to ramp local space
    const localPos = worldPos.clone().sub(position);

    // Rotate to ramp's local coordinate system (inverse of ramp rotation)
    const cos = Math.cos(-rotation);
    const sin = Math.sin(-rotation);
    const localX = localPos.x * cos - localPos.z * sin;
    const localZ = localPos.x * sin + localPos.z * cos;

    // Strict bounds checking - must be within ramp area
    const widthMargin = 0.5; // Small margin for bike width
    if (localX < -rampWidth / 2 - widthMargin || localX > rampWidth / 2 + widthMargin) {
      return null;
    }

    // Check Z bounds strictly - only on the actual ramp surface
    if (localZ < -rampLength / 2 || localZ > rampLength / 2) {
      return null;
    }

    // Calculate height based on position along ramp (Z axis)
    // localZ = -rampLength/2 → t = 0 → height = 0 (entry)
    // localZ = +rampLength/2 → t = 1 → height = rampHeight (exit)
    const t = (localZ + rampLength / 2) / rampLength;
    const clampedT = Math.max(0, Math.min(1, t));
    const surfaceHeight = clampedT * rampHeight;

    // Only apply ramp height if bike is close to the surface
    // This prevents snapping up when hitting the back wall
    const bikeY = worldPos.y;
    const maxHeightDiff = 1.5; // Max vertical distance to "catch" the ramp

    if (bikeY > surfaceHeight + maxHeightDiff) {
      // Bike is too high above the ramp surface (e.g., jumping over)
      return null;
    }

    if (bikeY < surfaceHeight - maxHeightDiff && surfaceHeight > 0.5) {
      // Bike is too low and ramp surface is elevated - hitting the wall from behind
      return null;
    }

    return surfaceHeight;
  }

  private createHoopMesh(): THREE.Group {
    const group = new THREE.Group();

    // Main hoop ring
    const hoopRadius = 4;
    const tubeRadius = 0.3;
    const hoopGeom = new THREE.TorusGeometry(hoopRadius, tubeRadius, 16, 32);
    const hoopMat = new THREE.MeshStandardMaterial({
      color: 0xffff00,
      emissive: 0xffff00,
      emissiveIntensity: 1.5,
    });
    const hoop = new THREE.Mesh(hoopGeom, hoopMat);
    hoop.rotation.y = Math.PI / 2;
    group.add(hoop);

    // Inner glow ring
    const innerGlowGeom = new THREE.TorusGeometry(hoopRadius - 0.5, 0.1, 8, 32);
    const innerGlowMat = new THREE.MeshStandardMaterial({
      color: 0xff8800,
      emissive: 0xff8800,
      emissiveIntensity: 2,
      transparent: true,
      opacity: 0.7,
    });
    const innerGlow = new THREE.Mesh(innerGlowGeom, innerGlowMat);
    innerGlow.rotation.y = Math.PI / 2;
    group.add(innerGlow);

    // Support poles
    const poleMat = new THREE.MeshStandardMaterial({
      color: 0x333333,
      metalness: 0.8,
    });

    const leftPole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.2, 0.2, this.config.hoopHeight, 8),
      poleMat
    );
    leftPole.position.set(0, -this.config.hoopHeight / 2, -hoopRadius - 0.5);
    group.add(leftPole);

    const rightPole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.2, 0.2, this.config.hoopHeight, 8),
      poleMat.clone()
    );
    rightPole.position.set(0, -this.config.hoopHeight / 2, hoopRadius + 0.5);
    group.add(rightPole);

    // Chevron indicators pointing through hoop
    const chevronMat = new THREE.MeshStandardMaterial({
      color: 0x00ff00,
      emissive: 0x00ff00,
      emissiveIntensity: 1,
    });

    for (let i = 0; i < 2; i++) {
      const chevronGeom = new THREE.ConeGeometry(0.8, 1.5, 3);
      chevronGeom.rotateX(Math.PI / 2);
      const chevron = new THREE.Mesh(chevronGeom, chevronMat);
      chevron.position.x = -2 - i * 2;
      group.add(chevron);
    }

    this.hoopTriggerRadius = hoopRadius;

    return group;
  }

  public checkHoopPass(playerPosition: THREE.Vector3): boolean {
    if (this.hoopPassed) return false;

    const hoopPos = this.hoopMesh.position;
    const distance = playerPosition.distanceTo(hoopPos);

    if (distance < this.hoopTriggerRadius) {
      this.hoopPassed = true;
      this.onHoopPass();
      return true;
    }

    return false;
  }

  private onHoopPass(): void {
    // Visual feedback - flash the hoop
    this.hoopMesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mat = child.material as THREE.MeshStandardMaterial;
        if (mat.emissive) {
          const original = mat.emissive.clone();
          mat.emissive.setHex(0x00ff00);
          mat.emissiveIntensity = 5;

          setTimeout(() => {
            mat.emissive.copy(original);
            mat.emissiveIntensity = 1.5;
          }, 500);
        }
      }
    });

    // Emit event for scoring
    EventBus.emit('target:destroyed', {
      id: 'hoop_bonus',
      position: {
        x: this.hoopMesh.position.x,
        y: this.hoopMesh.position.y,
        z: this.hoopMesh.position.z,
      },
      points: 500,
    });
  }

  public resetHoop(): void {
    this.hoopPassed = false;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public getHoopPosition(): THREE.Vector3 {
    return this.hoopMesh.position.clone();
  }

  public destroy(): void {
    this.scene.remove(this.mesh);
    this.scene.remove(this.hoopMesh);
    this.physicsWorld.removeBody(this.body);
  }
}
